import { useEffect } from 'react';
import { checkAuthStatus, getApiBaseUrl } from '../utils/api';

const LoginPage = () => {
  // DEBUG: Check environment loading
  console.log('=== ENVIRONMENT DEBUG ===');
  console.log('VITE_AUTH_MODE:', import.meta.env.VITE_AUTH_MODE);
  console.log('VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
  console.log('All VITE vars:', Object.keys(import.meta.env).filter(key => key.startsWith('VITE_')));
  console.log('========================');

  // Get auth mode from environment variable, default to 'google'
  const authMode = import.meta.env.VITE_AUTH_MODE || 'google';
  console.log('Final authMode decision:', authMode);

  // Check if user is already authenticated on component mount
  useEffect(() => {
    const checkIfAlreadyAuthenticated = async () => {
      try {
        console.log('🔍 Checking if user is already authenticated...');
        const authStatus = await checkAuthStatus();

        if (authStatus.authenticated) {
          console.log('✅ User is already authenticated - redirecting to dashboard');
          window.location.href = '/dashboard';
        } else {
          console.log('ℹ️ User is not authenticated - showing login page');
        }
      } catch (error) {
        console.error('❌ Error checking authentication status:', error);
      }
    };

    checkIfAlreadyAuthenticated();
  }, []);

  // Local login handler - ONLY available when authMode === 'local'
  const handleLocalLogin = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;

    if (!email) {
      alert('Please enter an email address');
      return;
    }

    try {
      const apiBaseUrl = getApiBaseUrl();
      console.log('🔐 Attempting local login to:', `${apiBaseUrl}/api/auth/local-login`);
      console.log('📧 Email:', email);

      const response = await fetch(`${apiBaseUrl}/api/auth/local-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Essential for session cookies over HTTPS
        body: JSON.stringify({ email }),
      });

      console.log('📡 Local login response status:', response.status);
      console.log('📡 Local login response ok:', response.ok);

      if (response.ok) {
        console.log('✅ Local login POST successful');

        // Verify login worked by checking auth status
        console.log('🔍 Verifying login with /auth/me...');
        const authStatus = await fetch(`${apiBaseUrl}/api/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('📡 Auth status response:', authStatus.status, authStatus.ok);

        if (authStatus.ok) {
          const data = await authStatus.json();
          console.log('📊 Auth status data:', data);

          if (data.authenticated) {
            console.log('🎉 Login verification successful - redirecting to dashboard');
            // Login successful - navigate to dashboard
            window.location.href = '/dashboard';
            return;
          } else {
            console.log('❌ Auth status shows not authenticated');
          }
        } else {
          console.log('❌ Auth status request failed');
        }

        alert('Login verification failed. Please try again.');
      } else {
        const error = await response.json();
        console.log('❌ Local login failed with error:', error);
        alert(error.error || 'Login failed');
      }
    } catch (error) {
      console.error('💥 Local login error:', error);
      alert('Login failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-neutral-50 to-sage-50 flex items-center justify-center p-6">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 bg-grid-slate-100/[0.02] bg-[size:60px_60px]"></div>

      <div className="relative bg-white/70 backdrop-blur-sm rounded-2xl shadow-elegant border border-slate-200/50 max-w-md w-full p-8 lg:p-10">
        <div className="text-center mb-8">
          {/* Elegant logo/icon */}
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-sage-100 to-sage-200 rounded-2xl mb-6 shadow-soft">
            <svg className="w-8 h-8 text-sage-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>

          <h1 className="text-2xl lg:text-3xl font-display font-light text-slate-800 mb-3">
            Ekdunt Arts
          </h1>
          <p className="text-slate-600 font-light">Ganesh Idol Business Management</p>
        </div>

        {authMode === 'local' ? (
          // LOCAL AUTHENTICATION MODE - Completely isolated from Google OAuth
          <>
            <form onSubmit={handleLocalLogin} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-slate-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-sage-500/20 focus:border-sage-400 transition-all duration-200"
                  placeholder="Enter your email"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-sage-500 to-sage-600 text-white font-medium py-4 px-6 rounded-xl hover:from-sage-600 hover:to-sage-700 hover:shadow-soft transition-all duration-200 shadow-elegant"
              >
                Continue with Email
              </button>
            </form>

            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-amber-800">LOCAL AUTH MODE</span>
              </div>
              <p className="text-xs text-amber-700">
                Local authentication is enabled. Set VITE_AUTH_MODE=google for Google OAuth.
              </p>
            </div>
          </>
        ) : (
          // GOOGLE OAUTH MODE - When VITE_AUTH_MODE is not 'local' (defaults to 'google')
          <>
            {(() => {
              // Google OAuth login handler - only defined when in google mode
              const handleGoogleLogin = () => {
                const apiBaseUrl = getApiBaseUrl();
                window.location.href = `${apiBaseUrl}/auth/google`;
              };

              return (
                <>
                  <button
                    onClick={handleGoogleLogin}
                    className="w-full flex items-center justify-center gap-4 bg-white border border-slate-200/60 text-slate-700 font-medium py-4 px-6 rounded-xl hover:bg-slate-50/80 hover:border-slate-300/60 hover:shadow-soft transition-all duration-200 shadow-elegant group"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    <span className="group-hover:translate-x-0.5 transition-transform duration-200">
                      Continue with Google
                    </span>
                  </button>

                  <p className="text-center text-slate-400 text-sm mt-6 font-light">
                    Secure authentication powered by Google
                  </p>
                </>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
